/**
 * @file ConfirmChangesModal.component.tsx
 * @module app/modules/price-catalog/components/ConfirmChangesModal.component
 * @description Confirmation modal for price catalog changes.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import React, { useMemo } from "react";
import { Modal, Button, Table, Badge } from "react-bootstrap";

interface ConfirmChangesModalProps {
  show: boolean;
  onHide: () => void;
  changes: any[];
  onConfirm: () => void;
  isSaving: boolean;
  enableForAllWarehouses: boolean;
}

const ConfirmChangesModal: React.FC<ConfirmChangesModalProps> = ({
  show,
  onHide,
  changes,
  onConfirm,
  isSaving,
  enableForAllWarehouses,
}) => {
  // Count negative margins across all tracked quantity changes
  const negativeMarginCount = useMemo(() => {
    let count = 0;
    changes.forEach((variantData) => {
      Object.values(variantData.quantities || {}).forEach((qc: any) => {
        if (Number(qc.newSp) > 0 && Number(qc.newCp) >= Number(qc.newSp)) {
          count++;
        }
      });
    });
    return count;
  }, [changes]);

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="w-100 text-center fs-3 fw-bolder">
          Confirm Changes
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-4">
        {enableForAllWarehouses && (
          <div className="alert alert-info bg-info bg-opacity-10 text-info border-0 text-center fw-600 mb-4 rounded-3 py-3">
            <i className="bi bi-info-circle me-2"></i>
            These changes will be applied to <strong>All Warehouses</strong>.
          </div>
        )}

        {negativeMarginCount > 0 && (
          <div className="alert alert-danger bg-danger bg-opacity-10 text-danger border-0 text-center fw-600 mb-4 rounded-3 py-3">
            {negativeMarginCount} row{negativeMarginCount > 1 ? "s" : ""} will
            result in a zero or negative margin.
          </div>
        )}

        <h5 className="fs-16 fw-bold mb-3">Modifications</h5>

        <div className="table-responsive rounded border">
          <Table hover className="mb-0 align-middle">
            <thead className="bg-light">
              <tr className="text-muted fs-14">
                <th className="ps-4">Product / Variant</th>
                <th>Unit / Setting</th>
                <th>Old Value</th>
                <th>New Value</th>
                <th className="text-end pe-4"></th>
              </tr>
            </thead>
            <tbody>
              {changes.map((variantData, idx) => {
                // 1. Filter and render rows for Quantity CP/SP Changes
                const activeQuantities = Object.values(
                  variantData.quantities || {},
                ).filter(
                  (q: any) =>
                    Number(q.newCp) !== Number(q.oldCp) ||
                    Number(q.newSp) !== Number(q.oldSp),
                );

                const qtRows = activeQuantities.map((qc: any, qIdx: number) => {
                  const isNegativeMargin =
                    Number(qc.newSp) > 0 &&
                    Number(qc.newCp) >= Number(qc.newSp);
                  return (
                    <tr key={`${idx}-qt-${qIdx}`}>
                      <td className="ps-4">
                        <div className="fw-600 text-dark">
                          {variantData.productTitle}
                        </div>
                        <div className="text-muted fs-14">
                          {variantData.variantTitle}
                        </div>
                      </td>
                      <td className="text-muted fw-500">{qc.unitLabel}</td>
                      <td className="fw-500">
                        <div className="text-muted text-decoration-line-through">
                          CP: {qc.oldCp}
                        </div>
                        <div className="text-muted text-decoration-line-through">
                          SP: {qc.oldSp}
                        </div>
                      </td>
                      <td className="fw-500">
                        <div className="text-dark fw-bold">CP: {qc.newCp}</div>
                        <div className="text-dark fw-bold">SP: {qc.newSp}</div>
                      </td>
                      <td className="text-end pe-4">
                        {isNegativeMargin && (
                          <Badge
                            bg="danger"
                            className="bg-opacity-10 text-danger border border-danger border-opacity-25 px-2 py-1"
                          >
                            Negative margin
                          </Badge>
                        )}
                      </td>
                    </tr>
                  );
                });

                // 2. Render row if Draft PO was toggled
                const draftPORow =
                  variantData.draftPO !== variantData.oldDraftPO ? (
                    <tr key={`${idx}-draftpo`}>
                      <td className="ps-4">
                        <div className="fw-600 text-dark">
                          {variantData.productTitle}
                        </div>
                        <div className="text-muted fs-14">
                          {variantData.variantTitle}
                        </div>
                      </td>
                      <td className="text-muted fw-500">Draft PO</td>
                      <td className="fw-500">
                        <span className="text-muted text-decoration-line-through">
                          {variantData.oldDraftPO ? "Enabled" : "Disabled"}
                        </span>
                      </td>
                      <td className="fw-500 text-primary fw-bold">
                        {variantData.draftPO ? "Enabled" : "Disabled"}
                      </td>
                      <td></td>
                    </tr>
                  ) : null;

                // 2b. Render row if Days Of Inventory Holding was changed
                const doihRow =
                  Number(variantData.doih) !== Number(variantData.oldDoih) ? (
                    <tr key={`${idx}-doih`}>
                      <td className="ps-4">
                        <div className="fw-600 text-dark">
                          {variantData.productTitle}
                        </div>
                        <div className="text-muted fs-14">
                          {variantData.variantTitle}
                        </div>
                      </td>
                      <td className="text-muted fw-500">DOIH</td>
                      <td className="fw-500">
                        <span className="text-muted text-decoration-line-through">
                          {variantData.oldDoih}
                        </span>
                      </td>
                      <td className="fw-500 text-primary fw-bold">
                        {variantData.doih}
                      </td>
                      <td></td>
                    </tr>
                  ) : null;

                // 3. Render row if Tax Settings were toggled at product level
                const taxRow = variantData.isProductLevelTaxChange ? (
                  <tr key={`${idx}-tax`}>
                    <td className="ps-4">
                      <div className="fw-600 text-dark">
                        {variantData.productTitle}
                      </div>
                      <div className="text-muted fs-14">
                        {variantData.variantTitle}
                      </div>
                    </td>
                    <td className="text-muted fw-500">Tax Settings</td>
                    <td className="fw-500">
                      <div className="text-muted text-decoration-line-through">
                        Taxable: {variantData.oldIsTaxable ? "Yes" : "No"}
                      </div>
                      <div className="text-muted text-decoration-line-through">
                        Rate: {variantData.oldTaxPercentage}%
                      </div>
                    </td>
                    <td className="fw-500 text-primary fw-bold">
                      <div>Taxable: {variantData.isTaxable ? "Yes" : "No"}</div>
                      <div>Rate: {variantData.taxPercentage}%</div>
                    </td>
                    <td></td>
                  </tr>
                ) : null;

                return (
                  <React.Fragment key={idx}>
                    {qtRows}
                    {draftPORow}
                    {doihRow}
                    {taxRow}
                  </React.Fragment>
                );
              })}
            </tbody>
          </Table>
        </div>
      </Modal.Body>
      <Modal.Footer className="border-0 justify-content-center pb-4 pt-4">
        <Button
          variant="light"
          className="uw-btn uw-btn-light text-success fw-bold px-4 bg-success bg-opacity-10 border-0 me-3"
          onClick={onHide}
          disabled={isSaving}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          className="uw-btn uw-btn-primary fw-bold px-4"
          onClick={onConfirm}
          disabled={isSaving}
        >
          {isSaving ? "Saving..." : "Confirm & Save"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ConfirmChangesModal;
